import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, catchError, from, switchMap, tap, throwError } from 'rxjs';
import { AuthService } from '../../modules/auth/services/auth.service';
import { JwtResponse } from '../../modules/auth/models/jwt-response.model';
import { NetworkService } from '../services/network.service';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  authService: AuthService;
  refreshTokenInProgress = false;
  tokenRefreshedSource = new Subject();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  exclusionPaths: string[] = ['/auth/refresh-token', '/auth/logout', '/auth/session'];

  constructor(
    authService: AuthService,
    private _networkService: NetworkService
  ) {
    this.authService = authService;
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    request = request.clone({ withCredentials: true });

    this._networkService.getCurrentStatus().then(status => {
      if (!status.connected) this._networkService.handleStatus(status);
    });

    return from(this._networkService.getCurrentStatus()).pipe(
      switchMap(status => {
        if (status.connected) {
          return next.handle(request).pipe(
            catchError(error => {
              return this.handleResponseError(error, request, next);
            })
          );
        } else {
          this._networkService.handleStatus(status);

          return throwError(
            () =>
              new HttpErrorResponse({
                error: 'No hay conexión a internet. Petición cancelada.',
                status: 0,
                statusText: 'No Internet Connection',
                url: request.url
              })
          );
        }
      })
    );
  }

  private handleResponseError(
    error: HttpErrorResponse,
    request?: HttpRequest<unknown>,
    next?: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (error.status === 401 && !this.exclusionPaths.some(path => request?.url.includes(path))) {
      return this.refreshToken().pipe(
        switchMap(() => {
          return next!.handle(request!);
        }),
        catchError(e => {
          if (e.status != 401) {
            return this.handleResponseError(e);
          } else {
            this.logout();
            return throwError(() => e);
          }
        })
      );
    } else if (error.status === 403) {
      this.logout();
    }
    return throwError(() => error);
  }

  private refreshToken(): Observable<JwtResponse> {
    if (this.refreshTokenInProgress) {
      return new Observable(observer => {
        this.tokenRefreshed$.subscribe(value => {
          observer.next(value as any);
          observer.complete();
        });
      });
    } else {
      this.refreshTokenInProgress = true;
      return this.authService.refreshToken().pipe(
        tap(() => {
          this.refreshTokenInProgress = false;
          this.tokenRefreshedSource.next({});
        }),
        catchError((err: HttpErrorResponse) => {
          this.refreshTokenInProgress = false;
          this.tokenRefreshedSource.next({});
          return throwError(() => err);
        })
      );
    }
  }

  private logout() {
    this.authService.logout();
  }
}
